import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ROUTES } from '../../constants/routes';
import useOrgSettingsStore from '../../stores/orgSettingsStore';
import { usePermissions } from '../../context/PermissionContext';

/**
 * LabModuleSettings — the per-org "Enable Lab Module" toggle.
 *
 * Lives on the admin Settings page (not behind the lab gate) so an admin
 * can switch it on. Reads/writes the single source of truth in
 * orgSettingsStore (enable_lab_module). When off, the whole module is hidden.
 */
export default function LabModuleSettings() {
  const enableLabModule = useOrgSettingsStore((s) => s.enableLabModule);
  const labLoaded = useOrgSettingsStore((s) => s.labLoaded);
  const fetchLabSettings = useOrgSettingsStore((s) => s.fetchLabSettings);
  const setLabModule = useOrgSettingsStore((s) => s.setLabModule);
  const { refreshPermissions } = usePermissions();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!labLoaded) fetchLabSettings();
  }, [labLoaded, fetchLabSettings]);

  const onToggle = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await setLabModule(!enableLabModule);
      // The backend strips/restores the lab permission slugs based on this
      // flag, so re-fetch the session to pick up (or drop) them WITHOUT a
      // re-login — otherwise the admin hits a wrongful "no permission" on the
      // Lab Catalog right after enabling.
      await refreshPermissions();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-5 mt-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">🧪 Laboratory module</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
            Lab catalog, lab ordering on visits, the Lab Technician role &amp; queue, and lab
            reports in patient history. When off (the default), the app behaves exactly as today.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <button
            type="button"
            role="switch"
            aria-checked={enableLabModule}
            onClick={onToggle}
            disabled={busy}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60 ${
              enableLabModule ? 'bg-teal-600' : 'bg-slate-300 dark:bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                enableLabModule ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
          <span
            className={`text-xs font-semibold ${
              enableLabModule ? 'text-teal-700 dark:text-teal-300' : 'text-slate-500 dark:text-slate-400'
            }`}
          >
            {busy ? 'Saving…' : enableLabModule ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>

      {err && <div className="text-xs text-rose-600 dark:text-rose-400 mt-2">{err}</div>}

      {enableLabModule && !busy && (
        <div className="mt-4">
          <Link
            to={ROUTES.LAB_CATALOG}
            className="inline-flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-teal-700"
          >
            Open Lab Catalog →
          </Link>
        </div>
      )}
    </div>
  );
}
